import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TableRequiredFields } from '@repo/ui/data-table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/tooltip'
import Typography from '@repo/ui/typography'
import { useTranslation } from 'react-i18next'

import { EditableTableColumn } from '.'

export type TooltipValueTypes<T> =
  | number
  | string
  | undefined
  | ((data: T) => string | number | undefined)

type TooltipWrapperProps<T extends TableRequiredFields> = {
  children: React.ReactNode
  data: T
  column: EditableTableColumn<T>
  isInput?: boolean
}

export function EditableTableTooltip<T extends TableRequiredFields>(
  props: TooltipWrapperProps<T>
) {
  const { data, column, children, isInput } = props
  const { t } = useTranslation()
  const labels: string[] = []

  if (column.tooltip) {
    const { initialValue, olValue } = column

    const initValue = typeof initialValue === 'function' ? initialValue(data) : initialValue
    const olValueDisplay = typeof olValue === 'function' ? olValue(data) : olValue

    if (initValue !== undefined && initValue !== null)
      labels.push(`${t('editableTable.tooltip.initialValue')} : ${initValue}`)
    if (olValueDisplay !== undefined && olValueDisplay !== null)
      labels.push(`${t('editableTable.tooltip.olValue')} : ${olValueDisplay}`)
  }

  if (!column.tooltip || labels.length === 0) return children
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger
          type={'button'}
          onClick={(e) => e.preventDefault()}
          className={cn({
            'text-red bg-red-light rounded-full p-1 min-w-6':
              labels.length > 0 && (typeof children === 'string' || typeof children === 'number'),
            'border border-red rounded': labels.length > 0 && isInput
          })}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          align={'center'}
          className="h-full max-h-48 overflow-y-auto bg-white-hover border border-grey-light rounded-sm p-3 py-2"
        >
          <div className={'flex items-start flex-col'}>
            {labels.map((label, index) => (
              <Typography key={index} variant={'tag'}>
                {label}
              </Typography>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
